import { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Typography, Select, Space } from 'antd';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../utils/constants';
import { useTheme } from '../../contexts/useTheme';
import circuitsService, { type Circuit } from '../../api/circuitsService';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

interface LocationPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (lat: number, lon: number) => void;
  initialLat?: number | null;
  initialLon?: number | null;
}

export const LocationPickerModal = ({ open, onClose, onSelect, initialLat, initialLon }: LocationPickerModalProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loadingCircuits, setLoadingCircuits] = useState(false);
  const { theme } = useTheme();

  // Fetch circuits on first open
  useEffect(() => {
    if (open && circuits.length === 0) {
      setLoadingCircuits(true);
      circuitsService
        .getAllCircuits()
        .then((data) => setCircuits(data))
        .catch(() => { /* silently fail - circuits dropdown just won't populate */ })
        .finally(() => setLoadingCircuits(false));
    }
  }, [open, circuits.length]);

  // Place or move the marker and update selected coords
  const placeMarker = useCallback((lat: number, lon: number) => {
    setSelectedCoords({ lat, lon });
    if (marker.current) {
      marker.current.setLngLat([lon, lat]);
    } else if (map.current) {
      marker.current = new mapboxgl.Marker({ color: '#1890ff' })
        .setLngLat([lon, lat])
        .addTo(map.current);
    }
  }, []);

  // Initialize the map after the modal animation completes and the container is visible
  const initMap = useCallback(() => {
    if (!mapContainer.current || map.current) return;

    const hasInitial = initialLat != null && initialLon != null && !isNaN(initialLat) && !isNaN(initialLon);
    const center: [number, number] = hasInitial ? [initialLon!, initialLat!] : [0, 20];
    const zoom = hasInitial ? 8 : 1.5;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: theme === 'dark'
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // If there's an initial location, place a marker
    if (hasInitial) {
      placeMarker(initialLat!, initialLon!);
    }

    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      const roundedLat = Math.round(lat * 1000000) / 1000000;
      const roundedLon = Math.round(lng * 1000000) / 1000000;
      placeMarker(roundedLat, roundedLon);
    });
  }, [theme, initialLat, initialLon, placeMarker]);

  // Clean up on close
  useEffect(() => {
    if (!open) {
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setSelectedCoords(null);
    }
  }, [open]);

  // Handle circuit selection - fly to circuit and place marker
  const handleCircuitSelect = (circuitId: string) => {
    const circuit = circuits.find((c) => c.circuit_id === circuitId);
    if (!circuit || circuit.latitude == null || circuit.longitude == null) return;

    const lat = circuit.latitude;
    const lon = circuit.longitude;
    placeMarker(lat, lon);

    if (map.current) {
      map.current.flyTo({ center: [lon, lat], zoom: 12, duration: 1500 });
    }
  };

  const handleOk = () => {
    if (selectedCoords) {
      onSelect(selectedCoords.lat, selectedCoords.lon);
    }
    onClose();
  };

  // Build circuit options, filtering to those with coordinates
  const circuitOptions = circuits
    .filter((c) => c.latitude != null && c.longitude != null)
    .map((c) => ({
      label: `${c.display_name} (${c.country})`,
      value: c.circuit_id,
    }));

  return (
    <Modal
      title="Pick a Location"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="Add Location"
      okButtonProps={{ disabled: !selectedCoords }}
      width={700}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (visible) {
          initMap();
        }
      }}
    >
      <Space direction="vertical" style={{ width: '100%', marginBottom: 8 }} size={8}>
        <Typography.Text type="secondary">
          Select a circuit to jump to its location, or click on the map.
        </Typography.Text>
        <Select
          showSearch
          allowClear
          placeholder="Jump to a circuit..."
          style={{ width: '100%' }}
          loading={loadingCircuits}
          options={circuitOptions}
          onChange={handleCircuitSelect}
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
        />
        {selectedCoords && (
          <Typography.Text strong>
            Selected: {selectedCoords.lat}, {selectedCoords.lon}
          </Typography.Text>
        )}
      </Space>
      <div
        ref={mapContainer}
        style={{
          width: '100%',
          height: 400,
          borderRadius: 8,
          overflow: 'hidden',
        }}
      />
    </Modal>
  );
};
